/**
 * P3-32: Cryptotheology 面板。
 */
import { useCallback } from "react";
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";
import { Panel } from "../../components/Panel";
import { t } from "../../bridge/i18n";
import { TranscendenceUpgradeButton } from "./TranscendenceUpgradeButton";

interface CryptotheologySnapshot {
  visible: boolean;
  upgrades: { name: string }[];
}

function computeSnapshot(game: GamePage | null): CryptotheologySnapshot {
  if (!game) {
    return { visible: false, upgrades: [] };
  }
  const religion = game.religion as { transcendenceTier?: number };
  const science = game.science as { get?: (name: string) => { researched?: boolean } | null };
  const visible = !!science.get?.("cryptotheology")?.researched && (religion.transcendenceTier ?? 0) > 0;
  return {
    visible,
    upgrades: visible
      ? ((game.religion as { transcendenceUpgrades?: { name: string }[] }).transcendenceUpgrades ?? [])
      : [],
  };
}

function snapshotEq(a: CryptotheologySnapshot, b: CryptotheologySnapshot): boolean {
  if (a.visible !== b.visible || a.upgrades.length !== b.upgrades.length) {
    return false;
  }
  for (let i = 0; i < a.upgrades.length; i++) {
    if (a.upgrades[i].name !== b.upgrades[i].name) {
      return false;
    }
  }
  return true;
}

export function CryptotheologyPanel() {
  const { visible, upgrades } = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  if (!visible) {
    return null;
  }

  return (
    <Panel title={t("religion.panel.cryptotheology.label")}>
      <span id="cryptotheologyInfo" dangerouslySetInnerHTML={{ __html: t("religion.panel.cryptotheology.info") }} />
      <div>
        {upgrades.map((upgr) => (
          <TranscendenceUpgradeButton key={upgr.name} upgradeName={upgr.name} />
        ))}
      </div>
    </Panel>
  );
}
